import { useCallback } from "react";
import { Driver, Maybe, Stop } from "@api/graphql/generated/generated-types";
import { useDriverRouteDataProvider } from "../contexts/DriverRouteDataProviderContext";
import { useDriverRouteViewSelectionProvider } from "../contexts/DriverRouteViewSelectionProvider";
import { getEffectiveBaseTime } from "../utils/timeUtils";
import { validateRouteDetailsOrdering } from "@src/shipments/utils/TaskListOrderingValidator";
import moment from "moment-timezone";

/**
 * Handles ETA preview mutations and route validation
 * Provides functions to update ETAs and check route order validity
 */
export function useRouteEtaUpdates({
  driver,
  selectedStopDate,
  setIsValidOrder,
  setErrorMessage,
}: {
  driver: Driver | Maybe<Driver> | undefined;
  selectedStopDate: string | undefined;
  setIsValidOrder: (value: boolean) => void;
  setErrorMessage?: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const { previewEtaForRouteMutation } = useDriverRouteDataProvider();
  const { selectedBaseTime, setSelectedBaseTime } =
    useDriverRouteViewSelectionProvider();

  const updateEtaForRouteDetails = useCallback(
    (routeDetails: Stop[], baseTimeMoment?: moment.Moment) => {
      if (!driver) return;

      const stopIds = routeDetails.map((routeDetail) => routeDetail.id);
      const effectiveBaseTime = baseTimeMoment ?? selectedBaseTime;
      const adjustedBaseTime = getEffectiveBaseTime(
        effectiveBaseTime,
        selectedStopDate
      );

      if (baseTimeMoment) {
        setSelectedBaseTime(adjustedBaseTime);
      }

      const baseTimeInSeconds = adjustedBaseTime.unix();

      previewEtaForRouteMutation({
        variables: {
          driverId: driver.id,
          stopIds: stopIds,
          baseTime: baseTimeInSeconds,
        },
      });
    },
    [
      driver,
      selectedStopDate,
      selectedBaseTime,
      previewEtaForRouteMutation,
      setSelectedBaseTime,
    ]
  );

  const checkRouteOrder = useCallback(
    (routeDetails: Stop[]) => {
      // Stops here come from an instantiated route, so they all carry a
      // `stopSequenceNumber`. Disable the fixed-sequence skip so the
      // pick-up/drop-off/return rule still runs while the dispatcher manually
      // reorders the route.
      const { isValidOrder, errorMessage } = validateRouteDetailsOrdering(
        routeDetails,
        { skipFixedSequenceShipments: false }
      );

      if (!isValidOrder) {
        setErrorMessage?.(errorMessage || "Invalid route order");
        setIsValidOrder(false);
      } else {
        setIsValidOrder(true);
        setErrorMessage?.(null);
      }
    },
    [setIsValidOrder, setErrorMessage]
  );

  return { updateEtaForRouteDetails, checkRouteOrder };
}

